import { useEffect, useRef } from 'react';
import type { InstrumentKind } from '@/lib/instruments';
import type { InstrumentSound, Voice } from '@/lib/instrumentSound';

const VOICES: Record<InstrumentKind, Voice> = {
  piano: 'piano',
  vocal: 'voice',
  guitar: 'nylon',
  'electric-guitar': 'electric',
  drums: 'drums',
  handpan: 'handpan',
  violin: 'bowed',
};

const GUITAR = [82.41, 110, 146.83, 196, 246.94, 329.63];
const VIOLIN = [196, 293.66, 440, 659.25];
const VOCAL = [293.66, 329.63, 369.99, 440, 493.88, 587.33, 659.25, 739.99];
const KURD = [220, 233.08, 261.63, 293.66, 329.63, 349.23, 392, 440];
const KURD_SLOTS = [0, 1, 7, 2, 6, 3, 5, 4];
const WHITE_STEPS = [0, 2, 4, 5, 7, 9, 11];
const BLACK_AFTER = [0, 1, 3, 4, 5];

type Phrase = readonly (readonly [seconds: number, target: number])[];

const STRUM: Phrase = [
  [0, 0],
  [0.035, 1],
  [0.07, 2],
  [0.105, 3],
  [0.14, 4],
  [0.175, 5],
  [0.8, 1],
  [1, 3],
  [1.2, 4],
  [1.4, 5],
  [1.6, 4],
  [1.8, 3],
];

const PHRASES: Record<InstrumentKind, Phrase> = {
  piano: [
    [0, 0],
    [0.18, 2],
    [0.36, 4],
    [0.54, 7],
    [0.72, 9],
    [0.9, 11],
    [1.2, 9],
    [1.5, 7],
  ],
  guitar: STRUM,
  'electric-guitar': [
    [0, 0],
    [0.02, 1],
    [0.04, 2],
    [0.5, 0],
    [0.52, 1],
    [0.54, 2],
    [0.95, 1],
    [0.97, 2],
    [0.99, 3],
    [1.4, 1],
    [1.42, 2],
    [1.44, 3],
  ],
  violin: [
    [0, 1],
    [0.65, 2],
    [1.3, 3],
    [1.95, 2],
  ],
  vocal: [
    [0, 2],
    [0.45, 4],
    [0.9, 5],
    [1.35, 4],
    [1.8, 6],
    [2.3, 5],
  ],
  handpan: [
    [0, 0],
    [0.35, 1],
    [0.6, 3],
    [0.85, 2],
    [1.1, 5],
    [1.35, 4],
    [1.6, 7],
    [2, 0],
  ],
  drums: [
    [0, 0],
    [0, 2],
    [0.25, 2],
    [0.5, 1],
    [0.5, 2],
    [0.75, 2],
    [1, 0],
    [1, 2],
    [1.25, 0],
    [1.5, 1],
    [1.5, 2],
    [1.75, 3],
    [2, 4],
    [2, 5],
  ],
};

type Target =
  | {
      shape: 'string';
      x0: number;
      y0: number;
      x1: number;
      y1: number;
      width: number;
      dotted: boolean;
      value: number;
    }
  | { shape: 'key'; x: number; y: number; w: number; h: number; black: boolean; value: number }
  | { shape: 'pad'; x: number; y: number; r: number; rings: number; value: number };

interface Layout {
  targets: Target[];
  decor: (ctx: CanvasRenderingContext2D) => void;
}

function layout(kind: InstrumentKind, w: number, h: number): Layout {
  const m = Math.min(w, h);
  const circle = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void => {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
  };

  if (kind === 'guitar' || kind === 'electric-guitar') {
    const top = h * 0.3;
    const bottom = h * 0.7;
    const x0 = w * 0.06;
    const x1 = w * 0.94;
    return {
      targets: GUITAR.map((value, i) => {
        const y = top + ((bottom - top) * i) / (GUITAR.length - 1);
        return {
          shape: 'string',
          x0,
          y0: y,
          x1,
          y1: y,
          width: 2.2 - i * 0.25,
          dotted: false,
          value,
        };
      }),
      decor: (ctx) => {
        for (let n = 0; n <= 9; n += 1) {
          const x = x0 + (w * 0.46 * (1 - 0.9 ** n)) / (1 - 0.9 ** 9);
          ctx.lineWidth = n === 0 ? 3 : 1;
          ctx.beginPath();
          ctx.moveTo(x, top - 10);
          ctx.lineTo(x, bottom + 10);
          ctx.stroke();
        }
        ctx.lineWidth = 1;
        if (kind === 'guitar') {
          circle(ctx, w * 0.7, h * 0.5, h * 0.24);
          circle(ctx, w * 0.7, h * 0.5, h * 0.27);
        } else {
          for (const x of [w * 0.68, w * 0.78]) ctx.strokeRect(x, top - 14, 22, bottom - top + 28);
        }
      },
    };
  }

  if (kind === 'violin') {
    const cy = h * 0.5;
    const x0 = w * 0.08;
    const x1 = w * 0.8;
    const nut = h * 0.035;
    const bridge = h * 0.085;
    return {
      targets: VIOLIN.map((value, i) => {
        const offset = i - 1.5;
        return {
          shape: 'string',
          x0,
          y0: cy + offset * nut,
          x1,
          y1: cy + offset * bridge,
          width: 1.9 - i * 0.3,
          dotted: false,
          value,
        };
      }),
      decor: (ctx) => {
        ctx.beginPath();
        ctx.moveTo(x0, cy - nut * 2.2);
        ctx.lineTo(w * 0.62, cy - bridge * 2);
        ctx.lineTo(w * 0.62, cy + bridge * 2);
        ctx.lineTo(x0, cy + nut * 2.2);
        ctx.closePath();
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x1 + 6, cy - bridge * 2.3);
        ctx.quadraticCurveTo(x1 - 8, cy, x1 + 6, cy + bridge * 2.3);
        ctx.stroke();
      },
    };
  }

  if (kind === 'vocal') {
    return {
      targets: VOCAL.map((value, i) => {
        const y = h * 0.78 - (i * h * 0.56) / (VOCAL.length - 1);
        return {
          shape: 'string',
          x0: w * 0.06,
          y0: y,
          x1: w * 0.94,
          y1: y,
          width: 1,
          dotted: true,
          value,
        };
      }),
      decor: () => undefined,
    };
  }

  if (kind === 'handpan') {
    const cx = w / 2;
    const cy = h / 2;
    return {
      targets: [
        { shape: 'pad', x: cx, y: cy, r: m * 0.12, rings: 2, value: 146.83 },
        ...KURD.map((value, i): Target => {
          const angle = Math.PI / 2 + ((KURD_SLOTS[i] ?? 0) * Math.PI) / 4;
          return {
            shape: 'pad',
            x: cx + Math.cos(angle) * m * 0.29,
            y: cy + Math.sin(angle) * m * 0.29,
            r: m * 0.075,
            rings: 1,
            value,
          };
        }),
      ],
      decor: (ctx) => {
        circle(ctx, cx, cy, m * 0.42);
        circle(ctx, cx, cy, m * 0.44);
      },
    };
  }

  if (kind === 'drums') {
    const pad = (x: number, y: number, r: number, rings: number, value: number): Target => ({
      shape: 'pad',
      x: w * x,
      y: h * y,
      r: m * r,
      rings,
      value,
    });
    return {
      targets: [
        pad(0.5, 0.64, 0.2, 2, 0),
        pad(0.27, 0.56, 0.12, 2, 1),
        pad(0.15, 0.32, 0.1, 3, 2),
        pad(0.42, 0.3, 0.09, 2, 3),
        pad(0.74, 0.6, 0.13, 2, 4),
        pad(0.76, 0.28, 0.14, 3, 5),
      ],
      decor: () => undefined,
    };
  }

  const left = w * 0.06;
  const top = h * 0.3;
  const keyW = (w * 0.88) / 14;
  const keyH = h * 0.42;
  const whites: Target[] = [];
  const blacks: Target[] = [];
  for (let i = 0; i < 14; i += 1) {
    const octave = Math.floor(i / 7);
    const step = (WHITE_STEPS[i % 7] ?? 0) + octave * 12;
    const value = 261.63 * 2 ** (step / 12);
    whites.push({
      shape: 'key',
      x: left + i * keyW,
      y: top,
      w: keyW,
      h: keyH,
      black: false,
      value,
    });
    if (BLACK_AFTER.includes(i % 7) && i < 13) {
      blacks.push({
        shape: 'key',
        x: left + (i + 1) * keyW - keyW * 0.29,
        y: top,
        w: keyW * 0.58,
        h: keyH * 0.6,
        black: true,
        value: value * 2 ** (1 / 12),
      });
    }
  }
  return { targets: [...whites, ...blacks], decor: () => undefined };
}

export function InstrumentStage({
  kind,
  sound,
  demo,
}: {
  kind: InstrumentKind;
  sound: InstrumentSound | null;
  demo: number;
}): React.JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const soundRef = useRef<InstrumentSound | null>(null);
  const triggerRef = useRef<((index: number, velocity: number) => void) | null>(null);

  useEffect(() => {
    soundRef.current = sound;
  }, [sound]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const css = getComputedStyle(document.documentElement);
    const color = (name: string): string => css.getPropertyValue(`--color-${name}`).trim();
    const ink = color('on-midnight');
    const accent = color('on-midnight-accent');
    const ground = color('midnight-raised');
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const voice = VOICES[kind];
    const strings =
      kind === 'guitar' || kind === 'electric-guitar' || kind === 'violin' || kind === 'vocal';

    let width = 0;
    let height = 0;
    let shape: Layout = { targets: [], decor: () => undefined };
    let energy = new Float32Array(0);
    let frame = 0;
    let lastActivity = 0;
    let nextGhost = 0;
    let hovered = -1;
    let last: { x: number; y: number } | null = null;

    const trigger = (index: number, velocity: number, audible = true): void => {
      const target = shape.targets[index];
      if (!target) return;
      energy[index] = Math.max(energy[index] ?? 0, velocity);
      if (!audible) return;
      lastActivity = performance.now() / 1000;
      soundRef.current?.play(voice, target.value, velocity);
    };
    triggerRef.current = trigger;

    const stringY = (target: Extract<Target, { shape: 'string' }>, x: number): number => {
      const t = Math.min(1, Math.max(0, (x - target.x0) / (target.x1 - target.x0)));
      return target.y0 + (target.y1 - target.y0) * t;
    };

    const hit = (x: number, y: number): number => {
      for (let i = shape.targets.length - 1; i >= 0; i -= 1) {
        const target = shape.targets[i];
        if (
          target?.shape === 'key' &&
          x >= target.x &&
          x <= target.x + target.w &&
          y >= target.y &&
          y <= target.y + target.h
        ) {
          return i;
        }
        if (target?.shape === 'pad' && Math.hypot(x - target.x, y - target.y) <= target.r) return i;
      }
      return -1;
    };

    const draw = (time: number): void => {
      ctx.clearRect(0, 0, width, height);
      ctx.strokeStyle = ink;
      ctx.globalAlpha = 0.14;
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
      shape.decor(ctx);

      shape.targets.forEach((target, i) => {
        const e = energy[i] ?? 0;
        if (target.shape === 'string') {
          const amp = still ? 0 : target.dotted ? 16 : 7;
          ctx.beginPath();
          for (let s = 0; s <= 64; s += 1) {
            const t = s / 64;
            const wave = target.dotted
              ? Math.sin(t * 30 + time * 14 + i)
              : Math.sin(time * 55 + i * 1.7);
            const x = target.x0 + (target.x1 - target.x0) * t;
            const y =
              target.y0 + (target.y1 - target.y0) * t + e * amp * Math.sin(Math.PI * t) * wave;
            if (s === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.setLineDash(target.dotted && e < 0.05 ? [2, 7] : []);
          ctx.lineWidth = target.width;
          ctx.globalAlpha = (target.dotted ? 0.4 : 0.6) + 0.4 * e;
          ctx.strokeStyle = ink;
          ctx.stroke();
          if (e > 0.02) {
            ctx.globalAlpha = e;
            ctx.strokeStyle = accent;
            ctx.stroke();
          }
          return;
        }

        if (target.shape === 'key') {
          const y = target.y + e * 3;
          ctx.setLineDash([]);
          ctx.lineWidth = 1;
          ctx.globalAlpha = 1;
          if (target.black) {
            ctx.fillStyle = ground;
            ctx.fillRect(target.x, y, target.w, target.h);
          }
          if (e > 0.01) {
            ctx.globalAlpha = e * (target.black ? 0.8 : 0.5);
            ctx.fillStyle = accent;
            ctx.fillRect(target.x + 1, y, target.w - 2, target.h);
          }
          ctx.globalAlpha = 0.5;
          ctx.strokeStyle = ink;
          ctx.strokeRect(target.x + 0.5, y + 0.5, target.w - 1, target.h - 1);
          return;
        }

        ctx.setLineDash([]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = ink;
        for (let ring = 0; ring < target.rings; ring += 1) {
          ctx.globalAlpha = ring === 0 ? 0.5 : 0.18;
          ctx.beginPath();
          ctx.arc(target.x, target.y, target.r * (1 - ring * 0.24), 0, Math.PI * 2);
          ctx.stroke();
        }
        if (e > 0.01) {
          ctx.globalAlpha = e * 0.3;
          ctx.fillStyle = accent;
          ctx.beginPath();
          ctx.arc(target.x, target.y, target.r, 0, Math.PI * 2);
          ctx.fill();
          if (!still) {
            ctx.globalAlpha = e * 0.7;
            ctx.strokeStyle = accent;
            ctx.beginPath();
            ctx.arc(target.x, target.y, target.r * (1 + (1 - e) * 0.9), 0, Math.PI * 2);
            ctx.stroke();
          }
        }
      });
      ctx.globalAlpha = 1;
    };

    const loop = (now: number): void => {
      frame = requestAnimationFrame(loop);
      const time = now / 1000;
      const decay = strings ? 0.965 : 0.93;
      for (let i = 0; i < energy.length; i += 1) energy[i] = (energy[i] ?? 0) * decay;
      if (!still && time - lastActivity > 3.5 && time > nextGhost && shape.targets.length > 0) {
        trigger(Math.floor(Math.random() * shape.targets.length), 0.35, false);
        nextGhost = time + 1.6;
      }
      draw(time);
    };

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio, 2);
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      shape = layout(kind, width, height);
      energy = new Float32Array(shape.targets.length);
    };

    const point = (event: PointerEvent): { x: number; y: number } => {
      const box = canvas.getBoundingClientRect();
      return { x: event.clientX - box.left, y: event.clientY - box.top };
    };

    const move = (event: PointerEvent): void => {
      const p = point(event);
      if (strings) {
        if (last) {
          const speed = Math.hypot(p.x - last.x, p.y - last.y);
          const velocity = Math.min(1, 0.45 + speed / 40);
          shape.targets.forEach((target, i) => {
            if (target.shape !== 'string' || p.x < target.x0 || p.x > target.x1) return;
            const before = last ? last.y - stringY(target, last.x) : 0;
            const after = p.y - stringY(target, p.x);
            if (before !== 0 && Math.sign(before) !== Math.sign(after)) trigger(i, velocity);
          });
        }
      } else {
        const index = hit(p.x, p.y);
        if (index !== hovered && index >= 0) trigger(index, 0.6);
        hovered = index;
      }
      last = p;
    };

    const down = (event: PointerEvent): void => {
      const p = point(event);
      if (!strings) {
        const index = hit(p.x, p.y);
        if (index >= 0) trigger(index, 0.9);
        hovered = index;
        return;
      }
      let nearest = -1;
      let distance = 18;
      shape.targets.forEach((target, i) => {
        if (target.shape !== 'string') return;
        const d = Math.abs(p.y - stringY(target, p.x));
        if (d < distance) {
          distance = d;
          nearest = i;
        }
      });
      if (nearest >= 0) trigger(nearest, 0.85);
      last = p;
    };

    const leave = (): void => {
      last = null;
      hovered = -1;
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointerleave', leave);
    frame = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointerleave', leave);
      triggerRef.current = null;
    };
  }, [kind]);

  useEffect(() => {
    const timers = PHRASES[kind].map(([seconds, index]) =>
      window.setTimeout(() => triggerRef.current?.(index, 0.8), 300 + seconds * 1000),
    );
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [kind, demo]);

  return <canvas ref={canvasRef} className="mv-instrument-canvas" aria-hidden="true" />;
}
